/**
 * Lightweight i18n (STA-143) — flat key-value maps for `en` / `zh`.
 *
 * Why not react-i18next: this app has < 200 strings and no plural / date
 * formatting needs that would justify the dep weight. A hand-rolled map
 * keeps the bundle small and is easy to grep.
 *
 * Usage:
 *   const { t, lang, setLang } = useI18n();
 *   <h1>{t('hero.title')}</h1>
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Lang = 'en' | 'zh';
const STORAGE_KEY = 'mp-lang';

const DICTIONARIES = {
  en: {
    'brand': 'Soul-Tarot',
    'nav.draw': 'Draw',
    'nav.history': 'Journal',
    'nav.friends': 'Friends',
    'nav.account': 'Account',
    'nav.logout': 'Log out',
    'nav.pricing': 'Plans',
    'nav.login': 'Sign in',
    'nav.stardust_pill_title': 'Stardust balance — tap to open shop',

    // Landing
    'landing.eyebrow': 'MYSTICPATH · TAROT JOURNAL',
    'landing.title_a': 'Daily',
    'landing.title_b': 'Soul Dialogue',
    'landing.sub': 'Draw three cards, voice what is on your mind.\nYour mentor remembers, and stays with you next time.',
    'landing.cta_start': 'Start free',
    'landing.cta_pricing': 'View plans',
    'landing.cta_note': '3 free draws / week · no credit card needed',
    'landing.omen_eyebrow': 'DAILY OMEN',
    'landing.omen_title': 'One draw a day, listen to today’s sign',
    'landing.omen_sub': 'Write a line, flip a card. Build a streak and earn perks.',
    'landing.mentors_eyebrow': 'YOUR MENTORS',
    'landing.mentors_title': 'Find the mentor who fits you',
    'landing.feature_a_title': 'Living mentors',
    'landing.feature_a_body': 'Each mentor has their own voice and specialty.',
    'landing.feature_b_title': 'Remembers your story',
    'landing.feature_b_body': 'Cross-session memory — they pick up where you left off.',
    'landing.feature_c_title': 'Tarot + conversation',
    'landing.feature_c_body': 'Not a cold dictionary — a reflection you can talk through.',
    'landing.cta_today': 'Start today’s reading',

    // Login
    'login.eyebrow': 'SIGN IN',
    'login.title': 'Welcome back',
    'login.sub': 'Sign in with Google or Apple — no extra registration.',
    'login.google': 'Sign in with Google',
    'login.apple': 'Sign in with Apple',
    'login.consent_prefix': 'Signing in means you accept our',
    'login.terms': 'Terms',
    'login.and': 'and',
    'login.privacy': 'Privacy Policy',

    // Verdict labels
    'verdict.super_lucky': 'SUPER LUCKY',
    'verdict.lucky': 'LUCKY',
    'verdict.trust': 'TRUST',
    'verdict.caution': 'CAUTION',
    'verdict.super_lucky_cn': 'Great Fortune',
    'verdict.lucky_cn': 'Good Fortune',
    'verdict.trust_cn': 'Listen Within',
    'verdict.caution_cn': 'Tread Lightly',

    // Daily Omen
    'omen.placeholder': 'A line for today (default: checking in)',
    'omen.draw': 'DRAW NOW',
    'omen.your_note': 'Your line',
    'omen.streak': 'Day streak',
    'omen.tomorrow': 'Come back tomorrow for another draw.',

    // Reading flow
    'reading.pick_mentor': 'Choose your mentor',
    'reading.pick_spread': 'Pick a spread',
    'reading.pick_category': 'Pick a theme (optional)',
    'reading.skip': 'Skip →',
    'reading.intention_title': 'What would you like to ask?',
    'reading.intention_placeholder': 'e.g. I’m torn between two careers. Which way?',
    'reading.try_this': 'Try a template (tap to fill)',
    'reading.next': 'Next',
    'reading.back': 'Back',
    'reading.centering_title': 'Center yourself',
    'reading.centering_body': 'Close your eyes for a moment.\nHold your question in mind.',
    'reading.draw_begin': 'Begin draw',
    'reading.shuffling': 'Shuffling…',
    'reading.pick_n_of_pool': 'Pick {k} from {pool}',
    'reading.committing': 'Your mentor is reading the cards…',
    'reading.your_cards': 'Your cards',
    'reading.commit': 'Ask the mentor',
    'reading.clear': 'Clear picks',
    'reading.daily_used': 'Today: {used} / {max}',
    'reading.rate_limited_title': 'Daily quota reached',
    'reading.rate_limited_body': 'Free plan gets 1 draw / day; resets at 00:00. Premium = unlimited + free follow-ups.',
    'reading.see_plans': 'View plans',
    'reading.spread_cost': 'Cost: {n}★',
    'reading.spread_positions': '{n} positions',
    'reading.insufficient_stardust': 'Not enough Stardust ({have}★ / {need}★)',
    'reading.go_topup': 'Top up Stardust',

    // Friends
    'friends.title': 'Friends',
    'friends.subtitle': 'See what your friends drew today',
    'friends.you': 'You',
    'friends.no_draw': 'Checking in…',
    'friends.like': 'Reflect with ♡',
    'friends.add': 'Add a friend',
    'friends.empty': 'You haven’t added any friends yet.',

    // Star Shop
    'shop.title': 'Star Shop',
    'shop.tab_stardust': 'Buy Stardust',
    'shop.tab_subscribe': 'Subscribe',
    'shop.balance': 'Current balance',
    'shop.popular': 'POPULAR',
    'shop.best_value': 'BEST VALUE',
    'shop.bonus': '+{n} bonus',
    'shop.buy': 'Buy',
    'shop.cancel_anytime': 'Cancel anytime',
    'shop.close': 'Close',

    // Account
    'account.title': 'Account',
    'account.identity': 'Signed in as',
    'account.subscription': 'Subscription',
    'account.upgrade': 'Upgrade to Premium',
    'account.cancel_sub': 'Cancel subscription',
    'account.manage_payment': 'Manage payment (Stripe Portal)',
    'account.daily_usage': 'Today’s usage',
    'account.unlimited': 'Unlimited draws',
    'account.change_avatar': 'Change photo',
    'account.avatar_too_large': 'Image too large (max 2 MB)',
    'account.preferences': 'Preferences',
    'account.language': 'Language',

    // Footer
    'footer.copyright': '© 2026 Soul-Tarot',
    'footer.terms': 'Terms',
    'footer.privacy': 'Privacy',
    'footer.refund': 'Refund policy',
  },
  zh: {
    'brand': 'Soul-Tarot',
    'nav.draw': '抽牌',
    'nav.history': '歷史',
    'nav.friends': '好友',
    'nav.account': '帳號',
    'nav.logout': '登出',
    'nav.pricing': '方案',
    'nav.login': '登入',
    'nav.stardust_pill_title': 'Stardust 餘額 — 點擊開啟商店',

    'landing.eyebrow': 'MYSTICPATH · TAROT JOURNAL',
    'landing.title_a': '每日的',
    'landing.title_b': '心靈交流',
    'landing.sub': '抽三張牌，把心裡的問題說出來。\n導師會記得你說過的事，下次來繼續陪你。',
    'landing.cta_start': '免費開始',
    'landing.cta_pricing': '查看方案',
    'landing.cta_note': '每週 3 次免費 · 無需信用卡',
    'landing.omen_eyebrow': 'DAILY OMEN',
    'landing.omen_title': '每日一抽，靜聽今日的指引',
    'landing.omen_sub': '每天寫下一句話、翻開一張牌。連續打卡解鎖獎勵與專屬塔羅伴侶。',
    'landing.mentors_eyebrow': 'YOUR MENTORS',
    'landing.mentors_title': '找一位最適合你的導師',
    'landing.feature_a_title': '動態 AI 導師',
    'landing.feature_a_body': '每位導師有自己的語氣與專長，挑一位最適合你的。',
    'landing.feature_b_title': '記得你說過的話',
    'landing.feature_b_body': '跨對話記憶，導師會接續上次的對話與情緒脈絡。',
    'landing.feature_c_title': '塔羅 + 對話',
    'landing.feature_c_body': '不是冷冰冰的牌義字典，而是一場有對話的反思。',
    'landing.cta_today': '開始今天的占卜',

    'login.eyebrow': 'SIGN IN',
    'login.title': '歡迎回來',
    'login.sub': '使用 Google 或 Apple 帳號登入，無須額外註冊。',
    'login.google': '以 Google 登入',
    'login.apple': '以 Apple 登入',
    'login.consent_prefix': '登入即代表你同意',
    'login.terms': '服務條款',
    'login.and': '與',
    'login.privacy': '隱私權政策',

    'verdict.super_lucky': 'SUPER LUCKY',
    'verdict.lucky': 'LUCKY',
    'verdict.trust': 'TRUST',
    'verdict.caution': 'CAUTION',
    'verdict.super_lucky_cn': '大吉',
    'verdict.lucky_cn': '小吉',
    'verdict.trust_cn': '靜聽',
    'verdict.caution_cn': '謹慎',

    'omen.placeholder': '今天的一句話（預設：打卡）',
    'omen.draw': '抽今日運勢',
    'omen.your_note': '你的一句話',
    'omen.streak': '連續打卡',
    'omen.tomorrow': '明天再來抽一次。',

    'reading.pick_mentor': '選擇導師',
    'reading.pick_spread': '選擇牌陣',
    'reading.pick_category': '選擇主題（可略過）',
    'reading.skip': '略過 →',
    'reading.intention_title': '今天想問什麼？',
    'reading.intention_placeholder': '例如：我最近換工作的猶豫，到底該往哪走？',
    'reading.try_this': '試試這個（按一下填入）',
    'reading.next': '下一步',
    'reading.back': '← 重選',
    'reading.centering_title': '靜心',
    'reading.centering_body': '接下來，請閉上眼睛\n心中默想著你的問題',
    'reading.draw_begin': '開始抽牌',
    'reading.shuffling': '洗牌中…',
    'reading.pick_n_of_pool': '從 {pool} 張中選 {k} 張',
    'reading.committing': '老師正在凝視牌面…',
    'reading.your_cards': '你的牌',
    'reading.commit': '請老師解讀',
    'reading.clear': '清除選擇',
    'reading.daily_used': '今日已使用 {used} / {max} 次',
    'reading.rate_limited_title': '今日次數已用完',
    'reading.rate_limited_body': '免費版每日 1 次抽牌，明日 00:00 重置。升級 Premium 享每日無限抽牌 + 免費追問。',
    'reading.see_plans': '查看方案',
    'reading.spread_cost': '需要 {n}★',
    'reading.spread_positions': '{n} 張牌位',
    'reading.insufficient_stardust': 'Stardust 不足（{have}★ / 需 {need}★）',
    'reading.go_topup': '前往儲值',

    'friends.title': '好友',
    'friends.subtitle': '看看朋友今天抽到什麼',
    'friends.you': '你',
    'friends.no_draw': '尚未抽 · 打卡中',
    'friends.like': '送出 ♡',
    'friends.add': '加好友',
    'friends.empty': '還沒有加好友。',

    'shop.title': '星塵商店',
    'shop.tab_stardust': '儲值 Stardust',
    'shop.tab_subscribe': '訂閱',
    'shop.balance': '目前餘額',
    'shop.popular': '熱門',
    'shop.best_value': '最划算',
    'shop.bonus': '加贈 {n}',
    'shop.buy': '購買',
    'shop.cancel_anytime': '隨時可取消',
    'shop.close': '關閉',

    'account.title': '帳號',
    'account.identity': '登入身份',
    'account.subscription': '訂閱狀態',
    'account.upgrade': '升級 Premium',
    'account.cancel_sub': '取消訂閱',
    'account.manage_payment': '管理付款方式（Stripe Portal）',
    'account.daily_usage': '今日使用',
    'account.unlimited': '每日無限抽牌',
    'account.change_avatar': '更換頭像',
    'account.avatar_too_large': '檔案過大（最大 2 MB）',
    'account.preferences': '偏好設定',
    'account.language': '語言',

    'footer.copyright': '© 2026 Soul-Tarot',
    'footer.terms': '服務條款',
    'footer.privacy': '隱私權',
    'footer.refund': '退費政策',
  },
} as const;

type DictKey = keyof typeof DICTIONARIES.en;

function detectInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (stored === 'en' || stored === 'zh') return stored;
  } catch {
    // ignore
  }
  if (typeof navigator !== 'undefined') {
    const code = navigator.language.toLowerCase();
    if (code.startsWith('en')) return 'en';
  }
  return 'zh';
}

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: DictKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en' : 'zh-TW';
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback(
    (key: DictKey, params?: Record<string, string | number>) => {
      const dict = DICTIONARIES[lang] ?? DICTIONARIES.zh;
      let s: string = (dict as Record<string, string>)[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          s = s.replace(`{${k}}`, String(v));
        }
      }
      return s;
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
  return ctx;
}
